import type { FC } from 'react'
import { useState } from 'react'
import { ChevronRight, FileText, UserPlus, Calendar, ClipboardList, FileCheck } from 'lucide-react'
import type { WorkItem } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const actionQueueMeta = {
  description: 'Pending approval, assignment, planning, and review items requiring immediate attention across vessels.',
  endpoint: 'GET /pms/jobs/search?state=OVERDUE&companyId={cgaId}',
  logic: 'Aggregates overdue PMS jobs as action items. Each item is categorized by type (approve, assign, plan, review, action) and prioritized by criticality.',
}

type Props = {
  items: WorkItem[]
  onViewAll?: () => void
  onAction?: (item: WorkItem) => void
}

const iconMap: Record<string, typeof FileText> = {
  approve: FileText,
  assign: UserPlus,
  plan: Calendar,
  review: ClipboardList,
  action: FileCheck,
}

const priorityColor: Record<WorkItem['priority'], { text: string; bg: string; dot: string }> = {
  HIGH: { text: 'text-red-600', bg: 'bg-red-50', dot: 'bg-red-600' },
  MEDIUM: { text: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-600' },
  LOW: { text: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-600' },
}

type FilterTab = 'ALL' | 'HIGH' | 'MEDIUM' | 'LOW'

const ActionQueue: FC<Props> = ({ items, onViewAll, onAction }) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('ALL')

  const counts = {
    ALL: items.length,
    HIGH: items.filter((i) => i.priority === 'HIGH').length,
    MEDIUM: items.filter((i) => i.priority === 'MEDIUM').length,
    LOW: items.filter((i) => i.priority === 'LOW').length,
  }

  const filtered = activeFilter === 'ALL' ? items : items.filter((i) => i.priority === activeFilter)

  return (
    <div className='card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex-1'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-bold text-slate-900'>Actionables</h3>
          <CardInfoButton meta={actionQueueMeta} />
          <span className='text-[11px] text-slate-400'>(Work Queue)</span>
        </div>
        <button className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5' onClick={onViewAll}>
          View all <ChevronRight className='w-3 h-3' />
        </button>
      </div>
      <div className='flex items-center gap-1 mb-3 border-b border-slate-100 pb-2'>
        {(['ALL', 'HIGH', 'MEDIUM', 'LOW'] as const).map((tab) => (
          <button
            key={tab}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
              activeFilter === tab ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:bg-slate-50'
            }`}
            onClick={() => setActiveFilter(tab)}
          >
            {tab === 'ALL' ? 'All' : tab.charAt(0) + tab.slice(1).toLowerCase()} ({counts[tab]})
          </button>
        ))}
      </div>
      <div className='space-y-2.5'>
        {items.length > 0 ? filtered.map((item) => {
          const Icon = iconMap[item.type] || FileText
          const colors = priorityColor[item.priority]

          return (
            <div
              key={item.id}
              className='flex items-start gap-2.5 p-2.5 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all'
            >
              <div className={`w-7 h-7 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon className={`w-3.5 h-3.5 ${colors.text}`} />
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-[11px] text-slate-700 leading-relaxed'>{item.description}</p>
                <p className='text-[10px] text-slate-400'>{item.vessel}</p>
              </div>
              <div className='flex flex-col items-end gap-1 flex-shrink-0'>
                <span className='text-[10px] text-slate-400'>{item.dueLabel}</span>
                <span className={`text-[10px] font-semibold ${colors.text}`}>{item.priority}</span>
                <button
                  className='px-3 py-1 bg-blue-600 text-white text-[11px] font-medium rounded-lg hover:bg-blue-700 transition-colors'
                  onClick={() => onAction?.(item)}
                >
                  {item.actionLabel}
                </button>
              </div>
            </div>
          )
        }) : (
          <div className='py-10 text-center text-slate-400 text-xs'>No pending action items</div>
        )}
      </div>
    </div>
  )
}

export default ActionQueue
